import React, { useMemo } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import SummaryCardList, {
  SummaryItem,
} from "@/shared/components/SummaryCardList";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import ButtonizedTooltip from "@/shared/components/ButtonizedTooltip";
import SettlementButton from "@/features/my-order/features/settlement-management/components/SettlementButton";
import AppSelect from "@/features/line-sheet-sets/app-select";
import SettlementManagementCreditList from "@/features/my-order/features/settlement-management/components/credit/SettlementManagementCreditList";
import { useSetRecoilState } from "recoil";
import settlementManagementCreditLoadingScope from "@/features/my-order/features/settlement-management/stores/useSettlementManagementCreditLoadingScope";
import useSettlementManagementQuery from "@/features/my-order/features/settlement-management/hooks/useSettlementManagementQuery";
import {
  CreditInvoiceStatisticsByBoutique,
  GetCreditInvoiceListRequest,
} from "@/features/my-order/features/settlement-management/types";
import { Trans } from "react-i18next";

interface SettlementManagementCreditMainSectionProps {
  selectedBoutique?: CreditInvoiceStatisticsByBoutique;
  creditListParams: GetCreditInvoiceListRequest;
  isCreditListEnabled: boolean;
}

function SettlementManagementCreditMainSection({
  selectedBoutique,
  creditListParams,
  isCreditListEnabled,
}: SettlementManagementCreditMainSectionProps) {
  const { tTitle, tCurrencyString, tSentence } = useI18nHelper();
  const availableCredits = selectedBoutique?.remainingAmount ?? 0;
  const issuedCredits = selectedBoutique?.totalAmount ?? 0;
  const usedCredits = selectedBoutique?.usedAmount ?? 0;

  const summaryList: SummaryItem[] = [
    {
      title: tTitle("settlement:available_credits"),
      value: tCurrencyString(availableCredits),
      textColor: availableCredits > 0 ? "#1272EF" : "#444444",
      backgroundColor: availableCredits > 0 ? "#F0F9FF" : "inherit",
    },
    {
      title: tTitle("settlement:issued_credits"),
      value: tCurrencyString(issuedCredits),
      textStyle: "body-lg-medium",
    },
    {
      title: tTitle("settlement:used_credits"),
      value: tCurrencyString(usedCredits),
      textStyle: "body-lg-medium",
    },
  ];

  return (
    <>
      <Flex justifyContent={"space-between"} padding={"20px 16px 0 16px"}>
        <Text textStyle={"heading-sm"} color={"#444444"}>
          {selectedBoutique?.boutiqueName}
        </Text>
        <Flex gap={"4px"} alignItems={"center"}>
          <ButtonizedTooltip
            label={tSentence("settlement:what_is_credit")}
            contentProps={{
              width: "440px",
            }}
          >
            <TooltipContent />
          </ButtonizedTooltip>
        </Flex>
      </Flex>
      <Flex flexDirection={"column"} gap={"16px"} flex={1}>
        <Box paddingX={"16px"}>
          <SummaryCardList summaryList={summaryList} />
        </Box>
        <CreditFilterList />
        <SettlementManagementCreditList
          params={creditListParams}
          isEnabled={isCreditListEnabled}
        />
      </Flex>
    </>
  );
}

export default SettlementManagementCreditMainSection;

const CreditFilterList = () => {
  const { tTitle } = useI18nHelper();
  const { query, setQuery } = useSettlementManagementQuery();
  const setLandingScope = useSetRecoilState(
    settlementManagementCreditLoadingScope
  );

  const handleFilterClick = (filter?: boolean) => {
    setQuery((prev) => ({
      ...prev,
      isUsed__eq: filter,
    }));
    setLandingScope("LIST");
  };

  const handleOrderSelect = (orderBy?: string) => {
    setQuery((prev) => ({
      ...prev,
      orderBy,
    }));
    setLandingScope("LIST");
  };

  const optionList = useMemo(
    () => [
      {
        name: tTitle("settlement:recently_issued_order"),
        value: "-issued_on",
      },
      {
        name: tTitle("settlement:oldest_issued_order"),
        value: "issued_on",
      },
    ],
    [tTitle]
  );

  const selectedOrderName =
    optionList.find((option) => option.value === query.orderBy)?.name ??
    tTitle("settlement:recently_issued_order");

  return (
    <Flex
      justifyContent={"space-between"}
      alignItems={"center"}
      paddingX={"16px"}
    >
      <Flex gap={"8px"}>
        <SettlementButton
          onClick={() => handleFilterClick()}
          isSelected={query.isUsed__eq === undefined}
        >
          {tTitle("all")}
        </SettlementButton>
        <SettlementButton
          onClick={() => handleFilterClick(false)}
          isSelected={query.isUsed__eq === false}
        >
          {tTitle("available")}
        </SettlementButton>
        <SettlementButton
          onClick={() => handleFilterClick(true)}
          isSelected={query.isUsed__eq === true}
        >
          {tTitle("used_complete")}
        </SettlementButton>
      </Flex>
      <AppSelect
        borderWidth={"none"}
        disableActive={true}
        customTrigger={
          <Flex gap={"4px"} alignItems={"center"} className={"group"}>
            <Box
              color={"#8F8F8C"}
              _groupHover={{
                color: "#1272EF",
              }}
              _groupActive={{
                color: "#00417D",
              }}
            >
              <CustomIcon src={"arrows-up-down"} width={16} height={16} />
            </Box>
            <Text
              textStyle={"body-sm-bold"}
              color={"#444444"}
              _groupHover={{
                color: "#1272EF",
              }}
              _groupActive={{
                color: "#00417D",
              }}
            >
              {selectedOrderName}
            </Text>
          </Flex>
        }
        name={tTitle("sort_by")}
        value={query.orderBy}
        onSelect={handleOrderSelect}
        options={optionList}
      />
    </Flex>
  );
};

const TooltipContent = () => {
  const { tSentence } = useI18nHelper();

  const tooltipContentList = [
    {
      question: "settlement:credit_tooltip_question_what_is_credit",
      answer: "settlement:credit_tooltip_answer_what_is_credit",
    },
    {
      question: "settlement:credit_tooltip_question_how_is_it_issued",
      answer: "settlement:credit_tooltip_answer_how_is_it_issued",
    },
    {
      question: "settlement:credit_tooltip_question_how_is_it_used",
      answer: "settlement:credit_tooltip_answer_how_is_it_used",
    },
  ];
  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      {tooltipContentList.map((content, index) => (
        <Flex key={index} flexDirection={"column"} gap={"4px"}>
          <Text textStyle={"body-sm-bold"} color={"#363636"}>
            Q. {tSentence(content.question)}
          </Text>
          <Text textStyle={"body-sm-regular"} color={"#444444"}>
            A. <Trans i18nKey={content.answer} components={{ br: <br /> }} />
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};
